import { useEffect, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Divider,
  FormControl,
  Grid,
  InputAdornment,
  InputLabel,
  MenuItem,
  Modal,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { Toaster } from "react-hot-toast";
import SearchIcon from "@mui/icons-material/Search";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import AccountBalanceWalletOutlinedIcon from "@mui/icons-material/AccountBalanceWalletOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import CompareArrowsIcon from "@mui/icons-material/CompareArrows";
import {
  getDealerDirectory,
  getDealerDirectoryData,
} from "_controllers/officer";
import { toastMessage } from "utils/toastMessage";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import moment from "moment";
import { PageHeader, StatCard, SectionCard, EmptyState, TablePager } from "_shared/ui";
import styles from "./DealerDirectoryList.module.css";

const compareModalStyle = {
  position: "fixed",
  top: 0,
  right: 0,
  height: "100vh",
  width: { xs: "100%", sm: "85%", md: "75%" },
  bgcolor: "background.paper",
  color: "text.primary",
  borderLeft: "1px solid",
  borderColor: "divider",
  boxShadow: 24,
  pt: 2,
  pb: 0,
  px: 4,
  textAlign: "left",
  overflowY: "auto",
};

const DealerDirectoryList = ({ onSelectDealer }) => {
  const [search, setSearch] = useState("");
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [clubCategory, setClubCategory] = useState([]);
  const [dealerType, setDealerType] = useState([]);
  const [selectedClub, setSelectedClub] = useState("");
  const [selectedDealerType, setSelectedDealerType] = useState("");
  const [openModal, setOpenModal] = useState(false);

  const [totalPages, setTotalPages] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalSums, setTotalSums] = useState();

  const [compareData, setCompareData] = useState([]);

  const handleChange = (e) => {
    setSearch(e.target.value);
  };

  useEffect(async () => {
    await getDealerDirectory({
      currentPage,
      search,
      selectedClub,
      selectedDealerType,
    })
      .then((d) => {
        setClubCategory(d?.clubs?.filter((element) => element !== ""));

        setLoading(false);
      })
      .catch((err) => {
        setLoading(false);
        toastMessage("error fetching data", "error");
      });
  }, []);

  useEffect(async () => {
    await getDealerDirectoryData({
      currentPage,
      search,
      selectedClub,
      selectedDealerType,
    })
      .then((d) => {
        setData(d?.data);
        setTotalPages(d?.totalPage);
        setTotalSums(d?.totalSum);
        setDealerType(
          d?.distinctDealerTypes?.filter((element) => element !== "")
        );
        setLoading(false);
      })
      .catch((err) => {
        setLoading(false);
        toastMessage("error fetching data", "error");
      });
  }, [currentPage, search, selectedClub, selectedDealerType]);

  const handlePageChange = (event, page) => {
    setCurrentPage(page);
  };

  const updateCompareList = async (data) => {
    const isDataInArray = compareData.find((item) => item._id === data._id);
    if (!isDataInArray) {
      if (compareData.length < 3) {
        setCompareData((prevData) => [...prevData, data]);
      } else {
        toastMessage("You can select upto 3 dealers only !", "error");
      }
    } else {
      const updatedCompareData = compareData.filter(
        (item) => item._id !== data._id
      );
      setCompareData(updatedCompareData);
    }
  };

  const handleRowClick = (selectedData) => {
    onSelectDealer(selectedData);
  };

  const formatNumber = (number) => {
    let numberString = String(Math.abs(number));
    let groups = [];
    while (numberString.length > 0) {
      groups.unshift(numberString.slice(-3));
      numberString = numberString.slice(0, -3);
    }
    return (number < 0 ? "-" : "") + groups.join(",");
  };

  const calculatePercentage = (current, lastMonth) => {
    const percentage =
      isFinite(lastMonth) && lastMonth !== 0
        ? ((current - lastMonth) / lastMonth) * 100
        : 0;
    return isFinite(percentage) && !isNaN(percentage)
      ? percentage.toFixed(2)
      : 0;
  };

  useEffect(() => {
    setCompareData([]);
  }, [selectedClub, selectedDealerType]);

  // Small themed trend indicator used inside the sales column and tooltip.
  const TrendChip = ({ current, lastMonth }) => {
    const pct = calculatePercentage(current, lastMonth);
    const up = isFinite(lastMonth) && pct > 0;
    const down = pct < 0;
    const color = up ? "success.main" : down ? "error.main" : "text.secondary";
    return (
      <Box
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: 0.25,
          color,
          fontWeight: 700,
          fontSize: "0.8125rem",
        }}
      >
        {up ? (
          <TrendingUpIcon fontSize="small" />
        ) : down ? (
          <TrendingDownIcon fontSize="small" />
        ) : null}
        {pct}%
      </Box>
    );
  };

  const salesTrend = isFinite(totalSums?.lastMonthSales)
    ? Number(
        calculatePercentage(
          Math.floor(totalSums?.totalSaleValue),
          Math.floor(totalSums?.lastMonthSales)
        )
      )
    : undefined;

  return (
    <>
      <PageHeader
        title="Dealer Directory"
        subtitle="Browse dealers, compare performance, and open detailed profiles"
      />

      {/* Headline KPIs */}
      <Grid container spacing={2.5} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            label="#Dealers"
            value={totalSums?.totalCount ? formatNumber(totalSums?.totalCount) : 0}
            icon={StorefrontOutlinedIcon}
            color="primary"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            label="Sales (MTD)"
            value={`${formatNumber(
              Math.floor(totalSums?.totalSaleValue / 100000)
            )}${totalSums?.totalSaleValue > 0 ? " Lac" : ""}`}
            icon={PaymentsOutlinedIcon}
            color="success"
            trend={typeof salesTrend === "number" && !isNaN(salesTrend) ? salesTrend : undefined}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            label="Outstanding (TOT)"
            value={`${formatNumber(
              Math.floor(totalSums?.totalOutTotal / 100000)
            )}${
              totalSums?.totalOutTotal > 0 || totalSums?.totalOutTotal < 0
                ? " Lac"
                : ""
            }`}
            icon={AccountBalanceWalletOutlinedIcon}
            color="info"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            label="Overdue (AOD)"
            value={`${formatNumber(
              Math.floor(totalSums?.totalOverdue / 100000)
            )}${
              totalSums?.totalOverdue > 0 || totalSums?.totalOverdue < 0
                ? " Lac"
                : ""
            }`}
            icon={WarningAmberOutlinedIcon}
            color="warning"
          />
        </Grid>
      </Grid>

      {/* Toolbar: filters + search + compare action */}
      <SectionCard sx={{ mb: 3 }} contentSx={{ p: 2 }}>
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
            <FormControl size="small" sx={{ minWidth: 170 }}>
              <InputLabel id="club-label">Filter by Club</InputLabel>
              <Select
                labelId="club-label"
                label="Filter by Club"
                value={selectedClub}
                name="zone"
                onChange={(event) => setSelectedClub(event.target.value)}
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {clubCategory?.map((item) => (
                  <MenuItem key={item} value={item}>
                    {item}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 190 }}>
              <InputLabel id="dealertype-label">Filter by DealerType</InputLabel>
              <Select
                labelId="dealertype-label"
                label="Filter by DealerType"
                value={selectedDealerType}
                name="zone"
                onChange={(event) => setSelectedDealerType(event.target.value)}
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {dealerType?.map((item) => (
                  <MenuItem key={item} value={item}>
                    {item}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              gap: 2,
            }}
          >
            <TextField
              size="small"
              onChange={handleChange}
              value={search}
              name="search"
              label="Search Dealer Name, Code or Territory"
              sx={{ minWidth: { xs: "100%", sm: 340 } }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
            <Button
              variant="contained"
              startIcon={<CompareArrowsIcon />}
              onClick={() => setOpenModal(true)}
              disabled={compareData.length === 0 ? true : false}
            >
              Compare{compareData.length > 0 ? ` (${compareData.length})` : ""}
            </Button>
          </Box>
        </Box>
      </SectionCard>

      {/* Table */}
      <SectionCard disablePadding sx={{ mb: 3 }}>
        {loading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              py: 8,
            }}
          >
            <CircularProgress color="primary" />
          </Box>
        ) : data && data?.length > 0 ? (
          <TableContainer sx={{ width: "100%" }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell align="left">Compare</TableCell>
                  <TableCell align="center" sx={{ width: 120 }}>
                    Dealer Code
                  </TableCell>
                  <TableCell align="center">Name</TableCell>
                  <TableCell align="center">City</TableCell>
                  <TableCell align="center">Dealer Type</TableCell>
                  <TableCell align="center">Club</TableCell>
                  <TableCell align="center" sx={{ width: 200 }}>
                    Sales (MTD)
                  </TableCell>
                  <TableCell align="center">Outstanding (AOD)</TableCell>
                  <TableCell align="center">Overdue (AOD)</TableCell>
                  <TableCell align="center">SAS</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data?.map((d) => (
                  <TableRow
                    key={d._id}
                    hover
                    onClick={() => handleRowClick(d)}
                    sx={{ cursor: "pointer" }}
                  >
                    <TableCell align="center">
                      <Checkbox
                        checked={compareData?.includes(d)}
                        onChange={(e) => {
                          e.stopPropagation();
                          updateCompareList(d);
                        }}
                        onClick={(e) => e.stopPropagation()}
                        color="primary"
                      />
                    </TableCell>
                    <TableCell align="left">{d?.Kunnr}</TableCell>
                    <TableCell align="center">{d?.Name1}</TableCell>
                    <TableCell align="center">{d?.City1}</TableCell>
                    <TableCell align="center">{d?.Dltype}</TableCell>
                    <TableCell align="center">{d?.Class}</TableCell>
                    <TableCell align="left">
                      <div className={styles.tooltip}>
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-evenly",
                            alignItems: "center",
                          }}
                        >
                          <div>{formatNumber(Math.floor(d?.SaleVal))}</div>
                          <TrendChip
                            current={parseFloat(d?.SaleVal)}
                            lastMonth={parseFloat(d?.lastMonthSales)}
                          />
                        </div>

                        <Box
                          className={styles.tooltiptext}
                          sx={{
                            bgcolor: "background.paper",
                            color: "text.primary",
                            border: "1px solid",
                            borderColor: "divider",
                            boxShadow: 3,
                          }}
                        >
                          <Box sx={{ mb: 0.5 }}>
                            <TrendChip
                              current={parseFloat(d?.SaleVal)}
                              lastMonth={parseFloat(d?.lastMonthSales)}
                            />
                          </Box>
                          <p
                            style={{
                              textDecoration: "underline",
                              fontSize: "16px",
                              marginBottom: "10px",
                            }}
                          >
                            {Math.floor(
                              Math.floor(d?.SaleVal) -
                                Math.floor(d?.lastMonthSales)
                            )}
                          </p>
                          <Typography variant="caption" color="text.secondary">
                            {moment.utc().startOf("month").format("DDMMM")} -{" "}
                            {moment.utc().format("DDMMM")}
                          </Typography>
                          <p style={{ fontSize: "16px", fontWeight: "bolder" }}>
                            {formatNumber(Math.floor(d?.SaleVal))}
                          </p>
                          <Typography variant="caption" color="text.secondary">
                            {moment
                              .utc()
                              .subtract(1, "month")
                              .startOf("month")
                              .format("DDMMM")}{" "}
                            -{" "}
                            {moment.utc().subtract(1, "month").format("DDMMM")}
                          </Typography>
                          <p style={{ fontSize: "16px", fontWeight: "bolder" }}>
                            {formatNumber(Math.floor(d?.lastMonthSales))}
                          </p>
                        </Box>
                      </div>
                    </TableCell>
                    <TableCell align="center">
                      {formatNumber(Math.floor(d?.OutTot))}
                    </TableCell>
                    <TableCell align="center">
                      {formatNumber(Math.floor(d?.OverDue))}
                    </TableCell>
                    <TableCell align="center">
                      {formatNumber(Math.floor(d?.SasOut))}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <TablePager
              totalPages={totalPages}
              page={currentPage}
              onPageChange={(p) => handlePageChange(null, p)}
            />
          </TableContainer>
        ) : (
          <EmptyState
            title="No dealers found"
            description="Try adjusting your search or filters to find dealers."
          />
        )}
      </SectionCard>

      <Modal open={openModal} onClose={() => setOpenModal(false)}>
        <Box sx={compareModalStyle}>
          {compareData && compareData.length > 0 ? (
            <>
              <Typography
                variant="h6"
                sx={{ fontWeight: 700, textAlign: "center", mb: 1 }}
              >
                Dealer Comparison
              </Typography>
              <Divider />
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                {[...Array(3)].map((_, index) => {
                  const item = compareData[index];
                  return (
                    <Box
                      key={index}
                      sx={{
                        borderRight: index < 2 ? "3px dotted" : "none",
                        borderColor: "divider",
                        pr: "20px",
                        width: `${99 / 3}%`,
                        textAlign: "center",
                        mt: 4,
                      }}
                    >
                      {item && (
                        <>
                          <Box
                            sx={{
                              width: 70,
                              height: 80,
                              borderRadius: "50%",
                              bgcolor: "primary.main",
                              color: "primary.contrastText",
                              fontWeight: 700,
                              fontSize: 24,
                              display: "flex",
                              justifyContent: "center",
                              alignItems: "center",
                              mx: "auto",
                            }}
                          >
                            {item.Name1.slice(0, 1)}
                          </Box>
                          <table className={styles.compare_modal_table}>
                            <tr>
                              <th style={{ textAlign: "right" }}>Name</th>
                              <td>{item?.Name1}</td>
                            </tr>
                            <tr>
                              <th style={{ textAlign: "right" }}>City</th>
                              <td>{item?.City1}</td>
                            </tr>
                            <tr>
                              <th style={{ textAlign: "right" }}>
                                Dealer Type
                              </th>
                              <td>{item?.DlType}</td>
                            </tr>
                            <tr>
                              <th style={{ textAlign: "right" }}>Club</th>
                              <td>{item?.Class}</td>
                            </tr>
                            <tr>
                              <th style={{ textAlign: "right" }}>Sale - MTD</th>
                              <td>{item?.SaleVal}</td>
                            </tr>
                            <tr>
                              <th style={{ textAlign: "right" }}>
                                Outstanding(AOD)
                              </th>
                              <td>{item?.OutTot}</td>
                            </tr>
                            <tr>
                              <th style={{ textAlign: "right" }}>
                                Overdue(AOD)
                              </th>
                              <td>{item?.OverDue}</td>
                            </tr>
                            <tr>
                              <th style={{ textAlign: "right" }}>SAS</th>
                              <td>{item?.SasIn}</td>
                            </tr>
                            <tr>
                              <th style={{ textAlign: "right" }}>
                                Sale Tyre Cat
                              </th>
                              <td>{"NA"}</td>
                            </tr>
                          </table>
                          <Button
                            variant="contained"
                            sx={{ mt: 4, mb: 4 }}
                            onClick={() => handleRowClick(item)}
                          >
                            View profile
                          </Button>
                        </>
                      )}
                    </Box>
                  );
                })}
              </Box>
            </>
          ) : (
            <EmptyState
              title="No dealers selected"
              description="Select dealers from the list to compare them."
            />
          )}
        </Box>
      </Modal>
    </>
  );
};

export default DealerDirectoryList;
